import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import type { SummarySection } from '@arcanum-hq/shared-types';

interface SummaryCardProps {
  sections: SummarySection[];
}

/**
 * End-of-turn structured summary card (pre-spec-notes Decision 1, Phase 2
 * Session 4).
 *
 * Default-collapsed — preserves chat thread density. Uncollapse gives
 * screenshot-ready output for stakeholder sharing (the explicit mitigation
 * for the "stop restating panel state" prompt change in Phase 1; pre-spec-
 * notes risk register flags this trade-off).
 *
 * Mount: BELOW the message body (after `<MessageBubble>`), before any
 * trailing `<ToolErrorCallout>` block (Session 4 mount-order spec).
 */
export function SummaryCard({ sections }: SummaryCardProps) {
  const [expanded, setExpanded] = useState(false);

  if (sections.length === 0) return null;

  return (
    <div
      data-testid="summary-card"
      className="border border-panel-border bg-panel-bg rounded-[3px] max-w-[65%]"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className={clsx(
          'w-full flex items-center gap-2 px-3 py-2',
          'text-xs font-mono text-text-muted hover:text-text',
        )}
      >
        <ChevronRight
          className={clsx('w-3 h-3 transition-transform', expanded && 'rotate-90')}
        />
        <span className="tracking-[0.5px] uppercase">Summary</span>
        <span className="ml-auto text-text-dim">
          {sections.length} section{sections.length !== 1 ? 's' : ''}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 flex flex-col gap-2 text-xs font-mono">
          {sections.map((s, i) => (
            <div key={i}>
              <div className="font-semibold text-text-bright mb-0.5">{s.heading}</div>
              <div className="text-text-muted whitespace-pre-wrap">{s.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
